/**
 * DEFCAT-R2-11 — level-aware "Add" button labels.
 *
 * The add-button text uses the Structure label (R2-15) for the level being
 * ADDED, not a generic "Add location". Depth semantics on the location axis:
 *
 *   L0 (synthetic component-group root)        … structure level 0
 *     └─ first catalogue location level        … structure level 1
 *          └─ next catalogue location level    … structure level 2  …
 *
 * So "add a root location under an L0" creates a level-1 node → label = the
 * level-1 structure label ("Location group" for an Enercon-shaped tree, or
 * "Location" when there's only one location level). "Add child under a node at
 * catalogue-depth d" creates a node at structure level d+2.
 *
 * Type axis has no synthetic root: structure level == catalogue depth. Adding a
 * type root creates level 0; a child under a depth-d type creates level d+1.
 *
 * Falls back to a generic label when no structure label exists for the slot.
 * Pure + unit-tested.
 */
import type { CatalogueAxis } from "@aeriosiq/types";
import type { DerivedLevel } from "./structureDerive.js";
import { levelsForAxis } from "./structureDerive.js";

/**
 * The structure level a newly-added node will occupy.
 *
 * @param parentCatalogueDepth catalogue-tree depth (0-based) of the PARENT real
 *   entry the node is added under, or `null` to add at the axis root.
 */
export function addedNodeLevel(
  axis: CatalogueAxis,
  parentCatalogueDepth: number | null,
): number {
  if (axis === "location") {
    // Location structure level = catalogue depth + 1 (L0 occupies level 0).
    // Root add (no parent entry, only the L0 above) → catalogue depth 0 → lvl 1.
    return parentCatalogueDepth === null ? 1 : parentCatalogueDepth + 2;
  }
  // Type: structure level == catalogue depth.
  return parentCatalogueDepth === null ? 0 : parentCatalogueDepth + 1;
}

/**
 * "+ Add <label>" text for adding a node under a parent at the given catalogue
 * depth. Uses the derived structure label for the target level; falls back to a
 * generic "Add location"/"Add type" when that level has no structure entry.
 */
export function addButtonLabel(
  axis: CatalogueAxis,
  parentCatalogueDepth: number | null,
  derived: DerivedLevel[],
): string {
  const targetLevel = addedNodeLevel(axis, parentCatalogueDepth);
  const levels = levelsForAxis(derived, axis);
  const match = levels.find((l) => l.level === targetLevel);
  if (match && match.label.trim().length > 0) {
    return `Add ${match.label.toLowerCase()}`;
  }
  return axis === "location" ? "Add location" : "Add type";
}
